import { useState } from 'react'
import { Table, Button, Tag, Typography, Space, App, Modal, Input, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useAuth } from '../../../auth/AuthProvider'
import { approveTerminationRequest, rejectTerminationRequest } from '../services/termination-request-api'
import type { TerminationRequest, TerminationRequestStatus } from '../types/termination-request.types'
import { TerminationRequestStatusLabels, TerminationRequestStatusColors } from '../types/termination-request.types'

const { TextArea } = Input
const { Text } = Typography

interface TerminationRequestsTableProps {
  requests: TerminationRequest[]
  loading: boolean
  onRefresh: () => void
  onEdit?: (request: TerminationRequest) => void
  canApprove: boolean
}

export function TerminationRequestsTable({
  requests,
  loading,
  onRefresh,
  onEdit,
  canApprove
}: TerminationRequestsTableProps) {
  const { message } = App.useApp()
  const { user } = useAuth()
  const [reviewModalOpen, setReviewModalOpen] = useState(false)
  const [reviewType, setReviewType] = useState<'approve' | 'reject'>('approve')
  const [selectedRequest, setSelectedRequest] = useState<TerminationRequest | null>(null)
  const [reviewComments, setReviewComments] = useState('')
  const [reviewNotes, setReviewNotes] = useState('')

  const currentUserId = user?.profile?.sub

  const handleApprove = (request: TerminationRequest) => {
    setSelectedRequest(request)
    setReviewType('approve')
    setReviewNotes(request.notes || '')
    setReviewModalOpen(true)
  }

  const handleReject = (request: TerminationRequest) => {
    setSelectedRequest(request)
    setReviewType('reject')
    setReviewNotes(request.notes || '')
    setReviewModalOpen(true)
  }

  const handleReviewSubmit = async () => {
    if (!selectedRequest) return

    try {
      if (reviewType === 'approve') {
        await approveTerminationRequest(selectedRequest.id, { comments: reviewComments, notes: reviewNotes || undefined })
        message.success('Request approved successfully')
      } else {
        await rejectTerminationRequest(selectedRequest.id, { comments: reviewComments, notes: reviewNotes || undefined })
        message.success('Request rejected successfully')
      }
      setReviewModalOpen(false)
      setReviewComments('')
      setReviewNotes('')
      setSelectedRequest(null)
      onRefresh()
    } catch (error: any) {
      console.error('Failed to review request:', error)
      message.error(error.response?.data?.message || 'Failed to review request')
    }
  }

  const columns: ColumnsType<TerminationRequest> = [
    {
      title: 'Workspace',
      dataIndex: 'workspaceName',
      key: 'workspaceName',
      width: 300,
      sorter: (a, b) => a.workspaceName.localeCompare(b.workspaceName),
      render: (workspaceName: string, record: TerminationRequest) => (
        <Space size={8} wrap={false} style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
          <Text ellipsis style={{ maxWidth: '150px' }}>
            {workspaceName}
          </Text>
          <span>
            {record.tenantCode && (
              <Tooltip title="Tenant code">
                <Tag color="geekblue">{record.tenantCode.toLowerCase()}</Tag>
              </Tooltip>
            )}
            <Tooltip title="Workspace code">
              <Tag color="default">{record.workspaceCode.toLowerCase()}</Tag>
            </Tooltip>
          </span>
        </Space>
      )
    },
    {
      title: 'Inactive date',
      dataIndex: 'inactiveDate',
      key: 'inactiveDate',
      sorter: (a, b) => new Date(a.inactiveDate || 0).getTime() - new Date(b.inactiveDate || 0).getTime(),
      render: (date: string | null) => (date ? new Date(date).toLocaleDateString() : '-')
    },
    {
      title: 'Permanent deletion date',
      dataIndex: 'permanentDeletionDate',
      key: 'permanentDeletionDate',
      render: (date: string | null) => (date ? new Date(date).toLocaleDateString() : '-')
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      ellipsis: { showTitle: false },
      render: (reason: string | null) =>
        reason ? (
          <Tooltip title={reason} placement="topLeft">
            <span
              style={{
                display: 'block',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {reason}
            </span>
          </Tooltip>
        ) : (
          '-'
        )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => {
        const statusOrder: Record<TerminationRequestStatus, number> = { p: 1, a: 2, r: 3 }
        return statusOrder[a.status] - statusOrder[b.status]
      },
      render: (status: TerminationRequestStatus) => (
        <Tag color={TerminationRequestStatusColors[status]}>{TerminationRequestStatusLabels[status]}</Tag>
      )
    },
    {
      title: 'Requested by',
      dataIndex: 'requestedByName',
      key: 'requestedByName',
      sorter: (a, b) => (a.requestedByName || '').localeCompare(b.requestedByName || ''),
      render: (name: string | null, record: TerminationRequest) => name || record.requestedBy.substring(0, 8)
    },
    {
      title: 'Requested date',
      dataIndex: 'requestedDate',
      key: 'requestedDate',
      sorter: (a, b) => new Date(a.requestedDate).getTime() - new Date(b.requestedDate).getTime(),
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: TerminationRequest) => {
        const isOwnRequest = record.requestedBy === currentUserId
        const canEditRequest = record.status === 'p' && (canApprove || isOwnRequest)

        return (
          <Space>
            {canEditRequest && onEdit && (
              <Button size="small" onClick={() => onEdit(record)}>
                Edit
              </Button>
            )}
            {canApprove && record.status === 'p' && (
              <>
                <Button size="small" type="primary" ghost onClick={() => handleApprove(record)}>
                  Approve
                </Button>
                <Button size="small" danger onClick={() => handleReject(record)}>
                  Reject
                </Button>
              </>
            )}
          </Space>
        )
      }
    }
  ]

  return (
    <>
      <div
        className="termination-requests-table-wrapper"
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Table
          size="small"
          columns={columns}
          dataSource={requests}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content', y: 'calc(100vh - 245px)' }}
          pagination={false}
          style={{ flex: 1 }}
          expandable={{
            expandedRowRender: record => (
              <div style={{ padding: '12px', background: '#fafafa', borderRadius: '4px' }}>
                <p>
                  <strong>Reason:</strong> {record.reason || 'N/A'}
                </p>
                <p>
                  <strong>Notes:</strong> {record.notes || 'None'}
                </p>
                <p>
                  <strong>Permanent deletion date:</strong>{' '}
                  {record.permanentDeletionDate ? new Date(record.permanentDeletionDate).toLocaleDateString() : 'N/A'}
                </p>
                {record.reviewedBy && (
                  <>
                    <p>
                      <strong>Reviewed by:</strong> {record.reviewedBy}
                    </p>
                    <p>
                      <strong>Review date:</strong>{' '}
                      {record.reviewedDate ? new Date(record.reviewedDate).toLocaleString() : 'N/A'}
                    </p>
                    {record.reviewComments && (
                      <p>
                        <strong>Review comments:</strong> {record.reviewComments}
                      </p>
                    )}
                  </>
                )}
              </div>
            )
          }}
        />
      </div>

      <Modal
        title={reviewType === 'approve' ? 'Approve request' : 'Reject request'}
        open={reviewModalOpen}
        onOk={handleReviewSubmit}
        onCancel={() => {
          setReviewModalOpen(false)
          setReviewComments('')
          setReviewNotes('')
          setSelectedRequest(null)
        }}
        okText={reviewType === 'approve' ? 'Approve' : 'Reject'}
        okButtonProps={{ danger: reviewType === 'reject' }}
      >
        <p>
          Are you sure you want to {reviewType} the termination request for{' '}
          <strong>{selectedRequest?.workspaceName}</strong>?
        </p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Notes</label>
          <TextArea
            rows={2}
            placeholder="Add notes (optional)"
            value={reviewNotes}
            onChange={e => setReviewNotes(e.target.value)}
            maxLength={1000}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Review comments</label>
          <TextArea
            rows={3}
            placeholder="Add review comments (optional)"
            value={reviewComments}
            onChange={e => setReviewComments(e.target.value)}
          />
        </div>
      </Modal>
    </>
  )
}
